import { NodeType } from "../../types";

/**
 * JSDoc imports.
 *
 * @import {
 *   Design,
 *   DesignObject
 * } from '../../types';
 */

export default class ObjectsUsedIn {
    /**
     * Get a list of @see {DesignObject}s with duplicate names.
     *
     * @param {DesignObject[]} objects
     * @param {Design} serialized
     * @returns {Record<string, DesignObject[]>}
     */
    static getDuplicateObjectNamesMap(objects, serialized){
        const isCommonModuleApp = serialized.client === 'InteractionsCommon';
        const nonCommonModuleObjects = objects.filter(obj => !obj.props.moduleApp);
        
        const useObjects = (isCommonModuleApp) ? serialized.components.objects : nonCommonModuleObjects;
        
        const duplicateObjectsNamesMap = {};
        const distinctNodeTypes = (type, idx, self) => { 
            return self.indexOf(type) === idx;
        };
        const objNodeTypes = useObjects.map(obj => obj.props.nodeTypeName);
        const uniqueTypes = objNodeTypes.filter(distinctNodeTypes);
        
        uniqueTypes.forEach(type => {
            if(type !== 'Module Begin'){
                const objTypes = useObjects.filter(obj => obj.props.nodeTypeName === type);
                const typeNamesMap = {};
                objTypes.forEach(obj => {
                    typeNamesMap[obj.name] = typeNamesMap[obj.name] || [];
                    typeNamesMap[obj.name].push(obj);
                });
                
                Object.values(typeNamesMap).forEach(nm => {
                    if(nm.length > 1){
                        duplicateObjectsNamesMap[type] = duplicateObjectsNamesMap[type] || [];
                        duplicateObjectsNamesMap[type].push(nm)
                    }
                });
                
            }
            
            
        });
        
        return duplicateObjectsNamesMap
    
    }

    /**
     * @param {Design} serialized
     * @returns {DesignObject[]}
     */
    static getUnusedObjects(serialized){
        const unusedObjects = [];
        const isCommonModuleApp = serialized.client === 'InteractionsCommon';
        const nonCommonModuleObjects = serialized.components.objects.filter(obj => !obj.props.moduleApp);
        const objects = (isCommonModuleApp) ? serialized.components.objects : nonCommonModuleObjects;
        
        objects.filter(obj => obj._class !== 'BeginNodeModel').forEach(obj => {
            const objectsUsedIn = ObjectsUsedIn.getObjectUsedIn(obj.id, serialized);
            if(objectsUsedIn.filter(y => y.length > 0).length === 0){
                unusedObjects.push(obj);
            } 
        });
        
        return unusedObjects;
    }
    
    /**
     * Get all uses of the given @see objId within @see serialized.Z
     * @param {string} objId The ID of the object to find uses for.
     * @param {Design} serialized The serialized data of the app.
     */
    static getObjectUsedIn(objId, serialized, config){
        // object to find
        const usedInObject = serialized.components.objects.find(ob => ob.id === objId);
        // data structures
        const usedIn = [];
        const usedInFlows = [];
        const usedInModuleInputs = [];
        const usedInLocalVariables = [];
        const usedInObjects = [];
        const usedInGlobalIntents = [];
        const usedInSampleDialogs = [];
        const promptIdRefs = {};
        const playSavedPromptIdRefs = {};
        const dataNodeInstanceMap = {};
        const dataAccessNodeRefs = {};
        const promptIsReferenceableRefs = {};
        const excludeDisabledInFlow = config && config.excludeDisabledInFlow || false;
        const excludeSampleDialogs = config && config.excludeSampleDialogs || false;

        // find in modules
        serialized.components.modules.forEach(mod => {
            const {nodes} = mod.versions[1].flow;
            
            // prompts usedInLocalVariables
            if (usedInObject._class === "PromptNodeModel"&&usedInObject.name!=="") {
                const localVariables = mod.localVariables || [];
                localVariables.forEach(lv => {
                    if (lv.props.config.type&&lv.props.config.type==="PROMPT"&&lv.props.config.defaultValue === usedInObject.name) {
                        usedInLocalVariables.push({ mod:mod, variable: lv });
                    }
                });
            }

            nodes.forEach(n => {
                // usedInModuleInputs
                if (usedInObject._class === "PromptNodeModel" && n._class === "ModuleNodeModel"&&usedInObject.name!=="") {
                    if (n.props.config.inputs && n.props.config.inputs.length > 0) {
                        n.props.config.inputs.forEach(input => {
                            if (input.type === "value") {
                                const moduleIndex = serialized.components.modules.findIndex(mo => mo.id === n.props.parentComponentID);
                                // hot fix: user imported onminchannel module into app and this broke here
                                if(moduleIndex !== -1){
                                    const localVar = serialized.components.modules[moduleIndex].localVariables;
                                    localVar.forEach(lv => {
                                        var localVarIndex = input.argumentName.indexOf("mod." + lv.name);
                                        if (localVarIndex !== -1) {
                                            if (input.value === usedInObject.name) {
                                                usedInModuleInputs.push({ mod: mod, nod: n });
                                            }
                                        }
                                    });
                                }

                            }
                        });
                    }
                }

                // usedInFlows
                if (n.props.parentComponentID === objId) {
                    // DEVES2-1118: Mark prompts as unused if they are used in disabled modules
                    // DEVDES2-965: exclude disabledInFlow prompt from used in
                    if(excludeDisabledInFlow){
                        if(!mod.props.config.disabledInFlow){
                            if(!n.props.config.disabledInFlow){
                                usedInFlows.push(mod.id);
                            }
                        }
                    }
                    else {
                        usedInFlows.push(mod.id);
                    }
                    
                }
            });
        });

        serialized.components.objects.forEach(obj => {
            switch (obj._class) {
                case NodeType.withFiller:
                    if (obj.props.config.referenceId === objId) {
                        usedInObjects.push(obj.id);
                    }
                    break;
            }

            // capture prompts referenced in config.promptId of SavePromptNodeModel and PlaySavedPromptNodeModel nodes
            if(obj._class === "SavePromptNodeModel" || obj._class === "PlaySavedPromptNodeModel"){
                promptIdRefs[obj.props.config.promptId] = promptIdRefs[obj.props.config.promptId] || [];
                promptIdRefs[obj.props.config.promptId].push(obj);
            }

            // capture playSavedPrompts referenced in config.playSavedPromptId of SavePromptNodeModel nodes
            if(obj._class === "SavePromptNodeModel"){
                playSavedPromptIdRefs[obj.props.config.playSavedPromptId] = playSavedPromptIdRefs[obj.props.config.playSavedPromptId] || [];
                playSavedPromptIdRefs[obj.props.config.playSavedPromptId].push(obj);
            }

            if(obj._class === "DataNodeModel"){
                dataNodeInstanceMap[obj.props.parentComponentID] = dataNodeInstanceMap[obj.props.parentComponentID] || [];
                dataNodeInstanceMap[obj.props.parentComponentID].push(obj);
            }

            if(obj._class === "DataAccessCompletionNodeModel"){
                dataAccessNodeRefs[obj.props.config.dataAccessNode] = dataAccessNodeRefs[obj.props.config.dataAccessNode] || [];
                dataAccessNodeRefs[obj.props.config.dataAccessNode].push(obj);
            }

            if(obj._class === "PromptNodeModel"){
                if(obj.props.config.isReferenceable){
                    promptIsReferenceableRefs[obj.id] = promptIsReferenceableRefs[obj.id] || [];
                    promptIsReferenceableRefs[obj.id].push(obj);
                }
            }

        });

        if (!excludeSampleDialogs) {
            serialized.dialogs.forEach(dialog => {
                if (dialog.nodes) {
                    for (let i = 0; i < dialog.nodes.length; i++) {
                        const n = dialog.nodes[i];
                        if (n.type === "prompt" && n.parentComponentID === objId) {
                            usedInSampleDialogs.push(dialog.id);
                            break;
                        }
                    }
                }
            });
        }

        if(promptIsReferenceableRefs[objId]){
            usedInObjects.push(objId)
        }

        // prompts used in config.promptId of SavePromptNodeModel and PlaySavedPromptNodeModel nodes
        if(promptIdRefs[objId]){
            promptIdRefs[objId].forEach(obj => usedInObjects.push(obj.id));
        }
        if (usedInObject._class === "PromptNodeModel"&&usedInObject.name!=="") {
            serialized.globalIntents.forEach(gi => {
                if(gi.moduleProperties){
                    if (gi.moduleProperties.props.config.inputs && gi.moduleProperties.props.config.inputs.length > 0) {
                        if (gi.moduleProperties.props.config.inputs.findIndex(input => input.value === usedInObject.name) !== -1) {
                            usedInGlobalIntents.push({
                                gi,
                                type: 'inGlobalIntent'
                            });
                        }
                    }
                }
            });
        } else if (
            usedInObject._class === NodeType.custom
            && usedInObject.id === serialized.globalErrorHandling?.postProcessingCustom
        ) {
            usedIn.push({
                name: 'Post Processing Custom',
                subtitle: 'Global Error Handling',
                title: 'Settings'
            });
        }

        // prompts used in config.promptId of SavePromptNodeModel and PlaySavedPromptNodeModel nodes
        // playSavedPrompts used in config.playSavedPromptId of SavePromptNodeModel
        if(playSavedPromptIdRefs[objId]){
            playSavedPromptIdRefs[objId].forEach(obj => usedInObjects.push(obj.id));
        }

        // data node objects rerefenced in config.dataAccessNode of DataAccessCompletionNodeModel
        if(dataNodeInstanceMap[objId]){
            dataNodeInstanceMap[objId].forEach(inst => {
                if (dataAccessNodeRefs[inst.id]) {
                    dataAccessNodeRefs[inst.id].forEach(ref => usedInObjects.push(ref.props.parentComponentID));
                }
            });
        }
              
           // if this order is changed make sure to check all the places we are using Usedin  
        return [
            usedInFlows,
            usedInObjects,
            usedInGlobalIntents,
            usedInLocalVariables,
            usedInModuleInputs,
            usedInSampleDialogs,
            usedIn
        ];
    }
}